import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import Typography from '@mui/material/Typography'
import React from 'react'
import {
  EXPANDER_COLUMN_MIN_WIDTH,
  HEADER_CELLS,
  HEADER_CELL_MIN_WIDTHS,
  SUBSCRIPTIONS_TABLE_MIN_WIDTH,
} from '../constants'
import {
  AlertContainer,
  LoadingState,
  SubscriptionsTableContainer,
} from '../styles'
import { AdminSubscriptionSortKey, AdminSubscriptionTableRow } from '../utils'
import { SubscriptionRow } from './SubscriptionRow'

interface Props {
  error: string | null
  expandedRows: Record<string, boolean>
  loading: boolean
  onSort: (key: AdminSubscriptionSortKey) => void
  onToggleRow: (subscriptionId: string) => void
  sortDirection: 'asc' | 'desc'
  sortKey: AdminSubscriptionSortKey
  tableHeight: number | string
  tableMinHeight: number
  visibleRows: AdminSubscriptionTableRow[]
}

export const SubscriptionsTable: React.FC<Props> = ({
  error,
  expandedRows,
  loading,
  onSort,
  onToggleRow,
  sortDirection,
  sortKey,
  tableHeight,
  tableMinHeight,
  visibleRows,
}) => (
  <SubscriptionsTableContainer
    tableHeight={tableHeight}
    tableMinHeight={tableMinHeight}
  >
    {loading ? (
      <LoadingState
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={1}
      >
        <CircularProgress size={22} />
        <Typography variant="body2">
          Loading subscription activity...
        </Typography>
      </LoadingState>
    ) : error ? (
      <AlertContainer>
        <Alert severity="error">{error}</Alert>
      </AlertContainer>
    ) : visibleRows.length === 0 ? (
      <AlertContainer>
        <Alert severity="info">
          No subscriptions matched the current search and filter combination.
        </Alert>
      </AlertContainer>
    ) : (
      <Table
        stickyHeader
        aria-label="subscriptions table"
        sx={{ minWidth: SUBSCRIPTIONS_TABLE_MIN_WIDTH }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                minWidth: EXPANDER_COLUMN_MIN_WIDTH,
                width: EXPANDER_COLUMN_MIN_WIDTH,
              }}
            />
            {HEADER_CELLS.map((cell) => (
              <TableCell
                key={cell.id}
                align={cell.align}
                sx={{
                  minWidth: HEADER_CELL_MIN_WIDTHS[cell.id] ?? 140,
                  whiteSpace: 'nowrap',
                }}
              >
                {cell.sortKey ? (
                  <TableSortLabel
                    active={sortKey === cell.sortKey}
                    direction={sortKey === cell.sortKey ? sortDirection : 'asc'}
                    onClick={() => onSort(cell.sortKey!)}
                  >
                    {cell.label}
                  </TableSortLabel>
                ) : (
                  cell.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleRows.map((row) => (
            <SubscriptionRow
              key={`${row.id}-${row.unitNumber}`}
              row={row}
              isExpanded={Boolean(expandedRows[row.subscriptionId])}
              onToggleRow={onToggleRow}
            />
          ))}
        </TableBody>
      </Table>
    )}
  </SubscriptionsTableContainer>
)
